// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// usePendingReviewCount (T-504) — count of staged captures awaiting review for
// the active facility (hf_review_items, status in pending_review/review_required).
//
// Powers the "N pending" badge on the collapsed assistant pill so nothing staged
// is forgotten. Subscribes to Realtime so the badge stays live as captures land
// and as items are committed/rejected from inside the widget.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFacility } from "@/contexts/FacilityContext";

const PENDING_STATUSES = ["pending_review", "review_required"] as const;

export function usePendingReviewCount(): { count: number; refresh: () => void } {
  const { activeFacility } = useFacility();
  const [count, setCount] = useState(0);

  const facilityId = activeFacility?.id ?? null;

  const fetchCount = useCallback(async () => {
    if (!facilityId) {
      setCount(0);
      return;
    }
    const { count: c, error } = await supabase
      .from("hf_review_items")
      .select("id", { count: "exact", head: true })
      .eq("facility_id", facilityId)
      .in("status", PENDING_STATUSES as unknown as string[]);
    if (!error) setCount(c ?? 0);
  }, [facilityId]);

  useEffect(() => {
    fetchCount();
    if (!facilityId) return;

    const channel = supabase
      .channel(`assistant-pending-${facilityId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "hf_review_items",
          filter: `facility_id=eq.${facilityId}`,
        },
        () => fetchCount(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [facilityId, fetchCount]);

  return { count, refresh: fetchCount };
}
